import React from "react";
import { motion } from "framer-motion";
import { Mic, MicOff, Radio, Ear, Volume2, VolumeX } from "lucide-react";
import { Switch } from "./ui/switch";
import { Slider } from "./ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

const StatusPill = ({ active, orbState, wakeWordActive }) => {
  // Show what the assistant is doing right now – replaces the big orb.
  let label = "Standby";
  let dotClass = "bg-white/30";
  let ringClass = "border-white/10";
  let textClass = "text-white/60";
  let pulsing = false;

  if (active) {
    if (orbState === "speaking") {
      label = "Talking";
      dotClass = "bg-emerald-400";
      ringClass = "border-emerald-400/40 shadow-[0_0_18px_rgba(52,211,153,0.35)]";
      textClass = "text-emerald-200";
      pulsing = true;
    } else if (orbState === "thinking") {
      label = "Thinking";
      dotClass = "bg-amber-300";
      ringClass = "border-amber-300/40 shadow-[0_0_18px_rgba(252,211,77,0.30)]";
      textClass = "text-amber-100";
      pulsing = true;
    } else {
      label = "Listening";
      dotClass = "bg-blue-400";
      ringClass = "border-blue-400/40 shadow-[0_0_18px_rgba(59,130,246,0.35)]";
      textClass = "text-blue-100";
      pulsing = true;
    }
  } else if (wakeWordActive) {
    label = "Waiting for “Hey Glitch”";
    dotClass = "bg-blue-300/70";
    ringClass = "border-blue-400/25";
    textClass = "text-blue-200/80";
    pulsing = true;
  }

  return (
    <div
      data-testid="status-pill"
      data-status={active ? orbState : wakeWordActive ? "wake" : "idle"}
      className={`glass rounded-full px-4 py-2 flex items-center gap-2 border transition-colors ${ringClass}`}
    >
      <span className="relative flex h-2.5 w-2.5">
        {pulsing && (
          <span
            className={`absolute inset-0 rounded-full ${dotClass} opacity-70 animate-ping`}
          />
        )}
        <span className={`relative rounded-full h-2.5 w-2.5 ${dotClass}`} />
      </span>
      <span
        className={`text-xs font-mono uppercase tracking-[0.28em] ${textClass}`}
      >
        {label}
      </span>
    </div>
  );
};

export const MicControls = ({
  active,
  onToggle,
  connectionState,
  wakeWordEnabled,
  onWakeWordChange,
  wakeWordSupported,
  wakeWordActive,
  orbState,
  volume,
  onVolumeChange,
}) => {
  const isConnecting = connectionState === "connecting";
  const muted = volume <= 0.001;

  return (
    <div
      className="shrink-0 w-full pt-1 flex flex-col items-center gap-1.5"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.5rem)" }}
    >
      {/* Live status indicator (replaces the big orb) */}
      <StatusPill
        active={active}
        orbState={orbState}
        wakeWordActive={wakeWordActive}
      />

      {/* Single pill: Always listening + Hey Glitch */}
      <div
        data-testid="mode-toggle"
        className="glass rounded-full px-3 sm:px-4 py-2 flex items-center gap-2 sm:gap-4 max-w-[calc(100vw-2rem)]"
      >
        <div className="flex items-center gap-1.5">
          <Radio
            className="h-3.5 w-3.5 text-blue-300 shrink-0"
            aria-hidden="true"
          />
          <span className="text-[11px] sm:text-xs text-white/80 whitespace-nowrap">
            Always listening
          </span>
          <Switch
            data-testid="always-listening-switch"
            checked
            disabled
            aria-label="Always listening"
          />
        </div>

        <div className="h-4 w-px bg-white/10" />

        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                data-testid="wake-word-toggle"
                className={`flex items-center gap-1.5 ${
                  !wakeWordSupported ? "opacity-50" : ""
                }`}
              >
                <Ear
                  className={`h-3.5 w-3.5 shrink-0 ${
                    wakeWordActive ? "text-blue-300" : "text-white/50"
                  }`}
                  aria-hidden="true"
                />
                <span
                  className={`text-[11px] sm:text-xs font-mono uppercase tracking-widest whitespace-nowrap ${
                    wakeWordActive ? "text-blue-200" : "text-white/60"
                  }`}
                >
                  Hey Glitch
                </span>
                <Switch
                  data-testid="wake-word-switch"
                  disabled={!wakeWordSupported}
                  checked={wakeWordEnabled}
                  onCheckedChange={onWakeWordChange}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="glass-strong border-white/10 text-xs"
            >
              {wakeWordSupported
                ? "Say “Hey Glitch” hands-free to start"
                : "Wake word needs a Chromium/Safari browser"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Volume slider */}
      <div
        data-testid="volume-control"
        className="glass rounded-full px-4 py-2 flex items-center gap-3 w-[min(320px,calc(100vw-2rem))]"
      >
        <button
          type="button"
          data-testid="volume-mute-btn"
          onClick={() => onVolumeChange(muted ? 0.8 : 0)}
          className="text-white/70 hover:text-white transition-colors"
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted ? (
            <VolumeX className="h-4 w-4" />
          ) : (
            <Volume2 className="h-4 w-4" />
          )}
        </button>
        <Slider
          data-testid="volume-slider"
          value={[Math.round(volume * 100)]}
          onValueChange={(v) => onVolumeChange((v?.[0] ?? 0) / 100)}
          min={0}
          max={100}
          step={1}
          className="flex-1"
          aria-label="Playback volume"
        />
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/50 w-8 text-right tabular-nums">
          {Math.round(volume * 100)}
        </span>
      </div>

      {/* Big mic button */}
      <motion.button
        data-testid="mic-button"
        onClick={onToggle}
        disabled={isConnecting}
        whileTap={{ scale: 0.92 }}
        whileHover={{ scale: 1.04 }}
        className={`relative h-16 w-16 rounded-full flex items-center justify-center transition-colors focus:outline-none focus:ring-4 focus:ring-blue-500/40 ${
          active
            ? "bg-blue-500 shadow-[0_0_60px_rgba(59,130,246,0.7)]"
            : "bg-white/10 hover:bg-white/15 border border-white/15"
        }`}
      >
        {active && (
          <motion.span
            className="absolute inset-0 rounded-full border-2 border-blue-300"
            animate={{ scale: [1, 1.4, 1], opacity: [0.8, 0, 0.8] }}
            transition={{ duration: 1.6, repeat: Infinity }}
          />
        )}
        {isConnecting ? (
          <motion.div
            className="h-5 w-5 rounded-full border-2 border-blue-300 border-t-transparent"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        ) : active ? (
          <MicOff className="h-6 w-6 text-white" />
        ) : (
          <Mic className="h-6 w-6 text-white/90" />
        )}
      </motion.button>

      <div
        data-testid="mic-hint"
        className="text-[10px] uppercase tracking-[0.3em] text-white/40"
      >
        {isConnecting
          ? "Connecting…"
          : active
          ? "Tap to stop"
          : wakeWordActive
          ? "Say “Hey Glitch” or tap"
          : "Tap to start"}
      </div>
    </div>
  );
};
